/**
 * The first phone number and the first address, on an ADD-CONTACT form.
 *
 * Matt, 2026-08-25, twice: "contacts should have names, titles, email,
 * addresses, phone numbers - why hasnt this been completed?"
 *
 * The answer had been that 0040 made both sub-resources — any number of each,
 * each with its own purpose and primary flag, on the contact's own cards. That
 * is still true and still right; what was wrong was making it the ONLY way in.
 * Entering the first number meant save, find the row, expand it, add — four
 * steps for the thing you were told on the phone thirty seconds ago.
 *
 * So: one of each, inline, optional, and then the cards for everything after.
 * The SAME shapes and the SAME components the cards use (AddressFields,
 * PhoneField, the purpose selects), because a second way of typing an address is
 * how the two drift apart.
 *
 * Deliberately not on EDIT. By then the contact has cards of its own, and an
 * immediate-effect control does not belong inside a form with one explicit Save
 * (the property form's address does exactly this, for the same reason).
 */

import {
  EMPTY_ADDRESS_FORM,
  EMPTY_PHONE_FORM,
  type AddressEditable,
  type AddressFormValues,
  type ContactPurpose,
  type PhoneEditable,
  type PhoneFormValues,
} from '@propos/shared';
import type { ReactNode } from 'react';

import { AddressFields, type AddressField } from './AddressFields.js';
import { CheckboxField, PhoneField, SelectField, TextField } from './fields.js';

export interface ContactDetailDraft {
  readonly phone: PhoneFormValues | null;
  readonly address: AddressFormValues | null;
}

export const EMPTY_CONTACT_DETAIL: ContactDetailDraft = { phone: null, address: null };

/** The purpose list as options, first entry defaulted by the caller. */
function purposeOptions(purposes: readonly ContactPurpose[]): { value: string; label: string }[] {
  return purposes.map((purpose) => ({ value: purpose.id, label: purpose.name }));
}

export function ContactDetailBlock({
  value,
  onChange,
  phonePurposes,
  addressPurposes,
}: {
  readonly value: ContactDetailDraft;
  readonly onChange: (next: ContactDetailDraft) => void;
  readonly phonePurposes: readonly ContactPurpose[];
  readonly addressPurposes: readonly ContactPurpose[];
}): ReactNode {
  const phoneOptions = purposeOptions(phonePurposes);
  const addressOptions = purposeOptions(addressPurposes);
  // Bound as consts so the null checks below narrow inside the callbacks too.
  const phone = value.phone;
  const address = value.address;

  return (
    <div className="flex flex-col gap-4">
      {/* Opt IN rather than always-open: most contacts get a number, few get an
          address, and a dialog that opens seven address fields nobody will type
          is the one people stop reading. */}
      <CheckboxField
        label="Add a phone number"
        checked={value.phone !== null}
        onChange={(checked) => {
          onChange({
            ...value,
            phone: checked
              ? { ...EMPTY_PHONE_FORM, purposeId: phoneOptions[0]?.value ?? '' }
              : null,
          });
        }}
        disabledReason={
          phoneOptions.length === 0
            ? 'No phone purposes are set up yet — an administrator adds them under Reference data.'
            : undefined
        }
      />
      {phone === null ? null : (
        <div className="flex flex-col gap-4 rounded-ctl border border-hairline p-3">
          <SelectField
            label="Number purpose"
            required
            value={phone.purposeId}
            onChange={(next) => {
              onChange({ ...value, phone: { ...phone, purposeId: next } });
            }}
            options={phoneOptions}
          />
          <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
            <PhoneField
              label="Number"
              required
              value={phone.number}
              onChange={(next) => {
                onChange({ ...value, phone: { ...phone, number: next } });
              }}
            />
            <TextField
              label="Extension"
              value={phone.extension}
              onChange={(event) => {
                onChange({
                  ...value,
                  phone: {
                    ...phone,
                    extension: event.target.value.replace(/\D/g, '').slice(0, 8),
                  },
                });
              }}
              inputMode="numeric"
              maxLength={8}
              hint="Digits only"
            />
          </div>
        </div>
      )}

      <CheckboxField
        label="Add an address"
        checked={value.address !== null}
        onChange={(checked) => {
          onChange({
            ...value,
            address: checked
              ? { ...EMPTY_ADDRESS_FORM, purposeId: addressOptions[0]?.value ?? '' }
              : null,
          });
        }}
        disabledReason={
          addressOptions.length === 0
            ? 'No address purposes are set up yet — an administrator adds them under Reference data.'
            : undefined
        }
      />
      {address === null ? null : (
        <div className="flex flex-col gap-4 rounded-ctl border border-hairline p-3">
          <SelectField
            label="Address purpose"
            required
            value={address.purposeId}
            onChange={(next) => {
              onChange({ ...value, address: { ...address, purposeId: next } });
            }}
            options={addressOptions}
          />
          <AddressFields
            values={address}
            onChange={(field: AddressField, next: string) => {
              onChange({ ...value, address: { ...address, [field]: next } });
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The draft as the write contract wants it, or undefined where the person did
 * not opt in. Empty strings become null for the same reason they do everywhere
 * else: "" is an unfilled field, not a value.
 */
export function contactDetailPayload(draft: ContactDetailDraft): {
  phone?: PhoneEditable;
  address?: AddressEditable;
} {
  const out: { phone?: PhoneEditable; address?: AddressEditable } = {};
  if (draft.phone !== null && draft.phone.number.trim() !== '') {
    out.phone = {
      purposeId: draft.phone.purposeId,
      number: draft.phone.number.trim(),
      extension: draft.phone.extension.trim() || null,
      isPrimary: true,
      notes: null,
    };
  }
  if (draft.address !== null) {
    const a = draft.address;
    const filled = [a.streetNumber, a.streetName, a.addressLine2, a.city, a.postalCode].some(
      (part) => part.trim() !== '',
    );
    // A blank address block is not an address, and the database says so too
    // (0040's addresses_not_empty) — so it is dropped rather than refused.
    if (filled) {
      out.address = {
        purposeId: a.purposeId,
        streetNumber: a.streetNumber.trim() || null,
        streetName: a.streetName.trim() || null,
        streetSuffix: a.streetSuffix.trim() || null,
        addressLine2: a.addressLine2.trim() || null,
        city: a.city.trim() || null,
        state: a.state.trim() || null,
        postalCode: a.postalCode.trim() || null,
        attention: a.attention.trim() || null,
        isPrimary: true,
        notes: null,
      };
    }
  }
  return out;
}
